import type { SupportedLanguage } from 'algoliasearch/lite';
import type { PropsWithChildren } from 'react';

import { Configure, InstantSearch } from 'react-instantsearch';

import { AlgoliaMiddlewares } from './AlgoliaMiddlewares';
import { VirtualAlgoliaComponents } from './virtual/VirtualAlgoliaComponents';
import { PageTypes } from '~/utilities/constants';
import {
  ALGOLIA_FUTURE_SETTINGS,
  ATTRIBUTES_TO_RETRIEVE,
  NUMBER_OF_TILES_PER_PAGE,
} from '~/utilities/constants/algolia';
import { useConsentContext } from '~/utilities/context/dynamic/ConsentContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useAlgoliaABTestConfig } from '~/utilities/hooks/search/useAlgoliaABTestConfig';
import { useAlgoliaAttributesToRetrieve } from '~/utilities/hooks/search/useAlgoliaAttributesToRetrieve';
import { useAlgoliaClient } from '~/utilities/hooks/search/useAlgoliaClient';
import { useAlgoliaFilters } from '~/utilities/hooks/search/useAlgoliaFilters';
import { useAlgoliaRouterSync } from '~/utilities/hooks/search/useAlgoliaRouterSync';
import { useAlgoliaRuleContexts } from '~/utilities/hooks/search/useAlgoliaRuleContexts';
import { useAlgoliaSortingOptions } from '~/utilities/hooks/search/useAlgoliaSortingOptions';
import { useAlgoliaUserToken } from '~/utilities/hooks/search/useAlgoliaUserToken';

export const AlgoliaCore = ({ children }: PropsWithChildren) => {
  const { primaryIndex } = useAlgoliaSortingOptions();
  const routerSync = useAlgoliaRouterSync();
  const algoliaClient = useAlgoliaClient();
  const { language, pageType } = useAppContext();
  // SRP reads `/shop/all` category details only for its SAP CX facets and sorting
  const filters = useAlgoliaFilters({ includeCategoryFilter: pageType !== PageTypes.SEARCH });
  const { shouldReportAnalytics } = useConsentContext();
  const attributesToRetrieve = useAlgoliaAttributesToRetrieve(ATTRIBUTES_TO_RETRIEVE);
  const { ruleContexts } = useAlgoliaRuleContexts();
  const { userToken } = useAlgoliaUserToken();
  const { enableABTest, analyticsTags } = useAlgoliaABTestConfig();
  const { categoryDetails: { categoryCode } = {} } = useProductListContext();

  const queryLanguage = [language] as SupportedLanguage[];

  if (!(algoliaClient && routerSync && primaryIndex)) {
    return <></>;
  }

  return (
    <InstantSearch
      // The key prop is used to force a re-render of the InstantSearch component when the primaryIndex changes, which is necessary for the TK51 A/B test to work correctly.
      key={primaryIndex}
      searchClient={algoliaClient}
      indexName={primaryIndex}
      future={ALGOLIA_FUTURE_SETTINGS}
      routing={routerSync}
      insights={false}
    >
      <Configure
        analytics={shouldReportAnalytics}
        clickAnalytics
        attributesToRetrieve={attributesToRetrieve}
        attributesToHighlight={[]}
        queryLanguages={queryLanguage}
        hitsPerPage={NUMBER_OF_TILES_PER_PAGE}
        filters={filters}
        userToken={userToken}
        ruleContexts={ruleContexts}
        enableABTest={enableABTest}
        analyticsTags={analyticsTags}
      />

      <VirtualAlgoliaComponents key={categoryCode} />
      <AlgoliaMiddlewares />

      {children}
    </InstantSearch>
  );
};
